"use client";

import { Container } from "@/components/ui/Container";
import { Reveal } from "@/components/ui/Reveal";
import { SectionLabel } from "@/components/ui/SectionLabel";
import { SplitHeading } from "@/components/ui/SplitHeading";
import { SECTION_TITLE_CLASS } from "@/lib/typography";
import type { Capability } from "@/lib/content/types";

type CapabilitiesProps = {
  capabilities: Capability[];
  label?: string;
  title?: string;
  description?: string;
};

export function Capabilities({
  capabilities,
  label,
  title,
  description,
}: CapabilitiesProps) {
  if (!capabilities.length || !title) return null;

  return (
    <section id="capabilities" className="py-20 md:py-28 lg:py-32">
      <Container>
        <Reveal>
          <header className="mx-auto max-w-3xl text-center">
            {label ? (
              <div className="mb-5 flex justify-center">
                <SectionLabel>{label}</SectionLabel>
              </div>
            ) : null}
            <SplitHeading className={SECTION_TITLE_CLASS}>
              {title}
            </SplitHeading>
            {description ? (
              <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-muted md:text-lg">
                {description}
              </p>
            ) : null}
          </header>
        </Reveal>

        <div className="mt-14 grid gap-12 border-t border-white/[0.08] pt-14 md:mt-16 md:grid-cols-3 md:gap-10 md:pt-16 lg:gap-14">
          {capabilities.map((cap, index) => (
            <Reveal key={cap.title} delay={index * 0.06}>
              <article>
                <span className="mb-5 block font-mono text-xs text-muted/60">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <h3 className="text-xl font-medium tracking-tight md:text-2xl">
                  {cap.title}
                </h3>
                {cap.items?.length ? (
                  <ul className="mt-5 space-y-2.5">
                    {cap.items.map((item) => (
                      <li key={item} className="text-sm leading-relaxed text-muted">
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : cap.description ? (
                  <p className="mt-5 text-sm leading-relaxed text-muted md:text-[0.9375rem]">
                    {cap.description}
                  </p>
                ) : null}
              </article>
            </Reveal>
          ))}
        </div>
      </Container>
    </section>
  );
}
